import { z } from 'zod';
import { dateOnlySchema } from './common';
/**
 * The review engine's vocabulary.
 *
 * ── WHY THE CHECKS ARE A DECLARED LIST ──
 * Two consumers must agree on it exactly: the API runs the checks, and the UI
 * (and an MCP client) renders and filters them. A check the UI does not know
 * about renders as a bare id; a check the UI knows about but the API never runs
 * looks like a clean bill of health. One list, here.
 *
 * Every check states WHY it matters in words a CA would send to a client
 * unedited. A finding a CA has to translate before forwarding is a finding they
 * stop reading.
 */
export const CHECK_SEVERITIES = ['high', 'medium', 'low'];
export const REVIEW_CHECKS = [
    // ── GST: anything that makes a return wrong ──
    {
        id: 'customer_gstin_missing',
        title: 'Customer marked as registered but has no GSTIN',
        severity: 'high',
        group: 'GST',
        why: 'These invoices cannot go into GSTR-1 Table 4A without a GSTIN, and the customer cannot ' +
            'claim input credit on them. Get the GSTIN before filing.',
    },
    {
        id: 'customer_gstin_invalid',
        title: 'GSTIN fails its checksum',
        severity: 'high',
        group: 'GST',
        why: 'A GSTIN that fails its own check digit is a typo. It will be rejected on upload, and until ' +
            'it is corrected the customer cannot match these invoices in their 2B.',
    },
    {
        id: 'gstin_state_mismatch',
        title: 'GSTIN state does not match the place of supply',
        severity: 'high',
        group: 'GST',
        why: 'The GSTIN carries its own state in the first two digits. When it disagrees with the place ' +
            'of supply, one of them is wrong - and the CGST/SGST versus IGST split follows whichever is ' +
            'wrong. This is the error that gets noticed by the customer, not by us.',
    },
    {
        id: 'place_of_supply_missing',
        title: 'No place of supply on a GST invoice',
        severity: 'high',
        group: 'GST',
        why: 'Without it there is no way to tell an intra-state supply from an inter-state one, so the ' +
            'tax was charged on a guess. Every one of these needs looking at before the return goes in.',
    },
    {
        id: 'line_hsn_missing',
        title: 'Invoice line with no HSN or SAC code',
        severity: 'high',
        group: 'GST',
        why: 'Table 12 is built per HSN. Lines without one cannot be reported, and the HSN summary will ' +
            'not tie to the invoice total.',
    },
    {
        id: 'hsn_too_few_digits',
        title: 'HSN code shorter than the turnover requires',
        severity: 'medium',
        group: 'GST',
        why: 'Four digits are enough up to ₹5 crore of turnover; above that six are required. The portal ' +
            'flags short codes as a defect.',
    },
    // ── Exports: the invoice the CA files, and what FEMA asks after it ──
    {
        id: 'export_lut_missing',
        title: 'Export without IGST, and no LUT on file for its year',
        severity: 'high',
        group: 'Exports',
        why: 'An export goes out without IGST only under a Letter of Undertaking filed for that financial ' +
            'year (CGST Rule 96A). Without one the supply is taxable at the full rate. Record the ARN in ' +
            'Settings, or file the LUT on the portal first.',
    },
    {
        id: 'export_address_missing',
        title: 'Export invoice without the customer’s address or country',
        severity: 'medium',
        group: 'Exports',
        why: 'CGST Rule 46 requires an export invoice to carry the recipient’s name and address, the ' +
            'address of delivery and the country of destination. Fill them on the customer; the next PDF ' +
            'of an issued invoice still shows the address it was issued with, so edit the invoice as well.',
    },
    {
        id: 'export_edf_due',
        title: 'Export Declaration Form due for the month',
        severity: 'high',
        group: 'Exports',
        why: 'From 1 October 2026 an exporter of services declares every month’s export invoices in one ' +
            'EDF to its bank within 30 days of the month end (FEMA 23(R)/2026-RB, Reg 3(2)). Record the ' +
            'filing date and the bank’s acknowledgement under Exports once it is in.',
    },
    {
        id: 'export_realisation_due',
        title: 'Export payment not received, and the deadline is close',
        severity: 'medium',
        group: 'Exports',
        why: 'Export proceeds must be realised within nine months of the invoice (twelve if invoiced in ' +
            'rupees), unless the bank extends it. A year on, an LUT export unpaid becomes IGST payable ' +
            'with interest (Rule 96A). Chase it, or ask the bank for an extension.',
    },
    // ── Documents: the numbering an auditor asks about ──
    {
        id: 'invoice_number_gap',
        title: 'Gap in the invoice number sequence',
        severity: 'medium',
        group: 'Documents',
        why: 'Table 13 reports the serial range issued and how many were cancelled. A gap that is not a ' +
            'cancelled document is a question an auditor will ask, and it is easier to answer now than ' +
            'in three years.',
    },
    {
        id: 'invoice_duplicate_suspect',
        title: 'Possible duplicate invoice',
        severity: 'medium',
        group: 'Documents',
        why: 'Same customer, same amount, within a few days. Usually a double entry - which overstates ' +
            'both turnover and the tax payable on it.',
    },
    {
        id: 'invoice_backdated',
        title: 'Invoice dated into a period already filed',
        severity: 'high',
        group: 'Documents',
        why: 'The return for that period has gone in without this invoice. It needs an amendment, not a ' +
            'quiet edit.',
    },
    {
        id: 'invoice_overdue_90',
        title: 'Unpaid for more than 90 days',
        severity: 'low',
        group: 'Books',
        why: 'Worth a provision decision at year end, and worth chasing well before that. This is the one ' +
            'check that looks past the period under review: old debt is exactly what a month-by-month ' +
            'read would never surface.',
    },
    // ── Books: what makes the numbers unreliable ──
    {
        id: 'bank_uncategorised',
        title: 'Bank transactions still uncategorised',
        severity: 'medium',
        group: 'Books',
        why: 'Until these are classified the books do not reflect the bank, and neither the P&L nor the ' +
            'closing balance can be relied on.',
    },
    {
        id: 'expense_no_receipt',
        title: 'Expense over the threshold with no receipt attached',
        severity: 'low',
        group: 'Books',
        why: 'The one thing asked for in an assessment, and the hardest to find a year later.',
    },
    {
        id: 'expense_billable_unbilled',
        title: 'Billable expense never invoiced',
        severity: 'low',
        group: 'Books',
        why: 'Money spent on a client and not recovered. Bill it, or mark it non-billable.',
    },
    {
        id: 'round_number_posting',
        title: 'Invoice total is exactly round despite the tax',
        severity: 'low',
        group: 'Books',
        why: 'Tax computed from a rate almost never lands on a round total. When it does, the figure was ' +
            'usually worked backwards from what someone wanted to charge rather than forwards from the ' +
            'work, and the split between you and the customer may not be what either of you thinks.',
    },
];
const CHECK_BY_ID = new Map(REVIEW_CHECKS.map((c) => [c.id, c]));
export const reviewCheckById = (id) => CHECK_BY_ID.get(id);
/** One thing to look at. Carries enough for the UI to link straight to it. */
/**
 * What can be done about a finding, decided by the server so the screen and
 * the agent read the same list. `kind` is what the client opens; `href` is
 * where a link goes when the fix is a page rather than a sheet.
 */
export const REVIEW_ACTION_KINDS = [
    'open',
    'edit-customer',
    'edit-invoice',
    'void-invoice',
    'attach-receipt',
    'record-payment',
    'write-off',
    'categorise',
    'apply-hsn',
    'open-lock',
];
export const setAsideSchema = z
    .object({
    checkId: z.string().trim().min(1).max(60),
    entityType: z.string().trim().min(1).max(30),
    entityId: z.string().trim().min(1).max(80),
    reason: z.string().trim().min(3, 'Say why this is fine - the next person to review the books will read it').max(300),
    /** The finding's `fingerprint`, as served. Optional: a caller without it still gets the updated_at witness. */
    fingerprint: z.string().trim().min(1).max(64).optional(),
})
    .strict();
// ── Thresholds the checks use ─────────────────────────────────────────────
//
// Declared here rather than buried in the engine because every one of them
// appears in a finding's own wording ("over Rs 5,000"), and a CA who disagrees
// with a threshold needs to see what it is before they trust the finding.
/** An expense at or above this, with nothing attached, is worth a receipt. */
export const EXPENSE_RECEIPT_THRESHOLD = 5000;
/** Unpaid this long past its due date is a provision conversation. */
export const OVERDUE_DAYS = 90;
/** Two invoices to one customer, same total, this close together. */
export const DUPLICATE_WINDOW_DAYS = 7;
/**
 * Round-number detection, on invoice totals only.
 *
 * There used to be an expense variant. Reading a real month's output killed it:
 * rent and salaries are round because they were agreed round, and a round
 * expense worth chasing is a round expense with nothing supporting it - which
 * `expense_no_receipt` already reports, without the second near-duplicate row.
 */
export const ROUND_INVOICE_STEP = 10000;
export const runReviewQuerySchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    severity: z.enum(['all', ...CHECK_SEVERITIES]).default('all'),
    checkId: z.string().trim().max(60).optional(),
})
    .strict();
// ── Review queries: the CA asking the client a question ────────────────────
export const REVIEW_QUERY_STATUSES = ['open', 'answered', 'resolved'];
export const raiseReviewQuerySchema = z
    .object({
    entityType: z.enum(['invoice', 'customer', 'expense', 'bankTransaction', 'item', 'general']),
    entityId: z.string().trim().max(36).optional(),
    question: z.string().trim().min(1, 'Say what you need to know').max(2000),
    /** Links the query to the check that raised it, when one did. */
    checkId: z.string().trim().max(60).optional(),
})
    .strict();
export const answerReviewQuerySchema = z
    .object({ answer: z.string().trim().min(1, 'Write an answer').max(2000) })
    .strict();
export const listReviewQueriesSchema = z
    .object({
    status: z.enum(['all', ...REVIEW_QUERY_STATUSES]).default('open'),
    entityType: z.string().trim().max(30).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
// ── Period locks ──────────────────────────────────────────────────────────
export const lockPeriodSchema = z
    .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    /** Why it was locked - almost always "GSTR-1 filed for August 2026". */
    reason: z.string().trim().min(1, 'Say why this period is being locked').max(300),
})
    .strict()
    .refine((v) => v.from <= v.to, { message: 'The period ends before it starts', path: ['to'] });
//# sourceMappingURL=review.js.map